// Axios adapter wrapper — the single choke point for offline behavior.
// GETs are write-through cached and served from cache on network failure;
// mutations that fail with a network error are queued and answered with a
// synthetic 2xx so the existing optimistic UI keeps working. HTTP errors
// (4xx/5xx) pass through unchanged.
import axios from 'axios';
import { cacheGet, cachePut, cacheKeys, queueAdd } from './db';
import { notifyQueueChanged, patchCacheForWrite } from './queue';

const defaultAdapter = axios.getAdapter(axios.defaults.adapter);

export function cacheKeyFor(config) {
  const params = config.params
    ? '?' + new URLSearchParams(Object.entries(config.params).filter(([, v]) => v !== undefined && v !== null)).toString()
    : '';
  return `${config.url}${params}`;
}

function isNetworkError(err) {
  return !err.response;
}

function syntheticResponse(config, data, status = 200, fromCache = false) {
  return {
    data, status, statusText: 'OK',
    headers: fromCache ? { 'x-from-cache': '1' } : { 'x-queued': '1' },
    config, request: null,
  };
}

export default async function offlineAdapter(config) {
  const method = (config.method || 'get').toLowerCase();

  if (method === 'get') {
    try {
      const res = await defaultAdapter(config);
      cachePut(cacheKeyFor(config), res.data).catch(() => {});
      return res;
    } catch (err) {
      if (isNetworkError(err)) {
        const key = cacheKeyFor(config);
        let cached = await cacheGet(key).catch(() => null);
        if (!cached) {
          // Time-windowed queries (e.g. /tasks/occurrences?start=now…) never
          // repeat exactly; fall back to the newest entry for the same path.
          const path = key.split('?')[0];
          const ws = config.params?.workspace_id;
          const keys = await cacheKeys().catch(() => []);
          const candidates = keys.filter((k) => k.split('?')[0] === path
            && (!ws || k.includes(`workspace_id=${encodeURIComponent(ws)}`)));
          let best = null;
          for (const k of candidates) {
            const entry = await cacheGet(k).catch(() => null);
            if (entry && (!best || entry.ts > best.ts)) best = entry;
          }
          cached = best;
        }
        if (cached) return syntheticResponse(config, cached.data, 200, true);
      }
      throw err;
    }
  }

  // Mutations
  try {
    return await defaultAdapter(config);
  } catch (err) {
    if (!isNetworkError(err)) throw err;

    let body = config.data;
    if (typeof body === 'string') {
      try { body = JSON.parse(body); } catch { /* raw body (e.g. markdown import) */ }
    }

    let responseData = { _queued: true };
    if (method === 'post' && config.url === '/tasks' && body && typeof body === 'object') {
      if (!body.id) body = { ...body, id: crypto.randomUUID() };
      responseData = { ...body, created_at: new Date().toISOString() };
    } else if (body && typeof body === 'object') {
      responseData = { ...body, _queued: true };
    }

    const contentType = (config.headers && (config.headers['Content-Type'] || config.headers['content-type'])) || 'application/json';
    await queueAdd({ method, url: config.url, body, contentType: String(contentType) });
    await patchCacheForWrite(method, config.url, body);
    notifyQueueChanged();
    return syntheticResponse(config, responseData, method === 'post' ? 201 : 200);
  }
}
